// src/lib/playersService.js
// Firestore layer for the roster.
//
// The roster used to live in src/data/playersData.js (compiled into the bundle),
// which meant every change needed a code push. It now lives in Firestore so the
// team can manage it from the admin panel. The static file is still used as a
// fallback for the photos of players who were on the site before this migration.

import {
  collection,
  doc,
  addDoc,
  updateDoc,
  deleteDoc,
  getDocs,
  onSnapshot,
  serverTimestamp,
  writeBatch,
  Timestamp,
} from "firebase/firestore";
import { db } from "../firebase";
import staticPlayers from "../data/playersData";
import { deletePlayerImage } from "./imageUpload";

export const PLAYERS_COL = collection(db, "players");

// ─── Divisions ───────────────────────────────────────────────────────────────
// Single source of truth so the admin dropdown and the roster pages agree.
// `group` decides which public page a player appears on.
export const DIVISIONS = [
  { value: "CDL Team A",       label: "CDL — Team A",          group: "cdl" },
  { value: "CDL Substitute",   label: "CDL — Substitute",      group: "cdl" },
  { value: "CDL Coach",        label: "CDL — Coach",           group: "cdl" },
  { value: "CDL",              label: "CDL — Not assigned yet", group: "cdl" },
  { value: "Warzone",          label: "Warzone",               group: "warzone" },
  { value: "Warzone Substitute", label: "Warzone — Substitute", group: "warzone" },
  { value: "Female",           label: "Female Division",       group: "female" },
  { value: "U18",              label: "U18 Division",          group: "u18" },
  { value: "Content Creators", label: "Content Creator",       group: "creators" },
  // Ex-members who went pro. Their page is hidden until the team switches it on
  // from the admin panel (siteSettings/sections), but they can be managed here
  // like any other player in the meantime.
  { value: "Graduates",        label: "Graduates — Gone Pro",  group: "graduates" },
];

// Fixed running order for the divisions in the admin list. Without this the
// section order is emergent — whichever division happens to hold the lowest
// sort value floats to the top — which makes the page appear to shuffle itself
// whenever someone is added or moved.
export const DIVISION_ORDER = DIVISIONS.map((d) => d.value);

export const DIVISION_GROUPS = {
  cdl: ["CDL Team A", "CDL Substitute", "CDL Coach", "CDL"],
  warzone: ["Warzone", "Warzone Substitute"],
  female: ["Female"],
  u18: ["U18"],
  creators: ["Content Creators"],
  graduates: ["Graduates"],
};

// ─── Cross-listing ───────────────────────────────────────────────────────────
// Some players belong on more than one page — a Warzone player who also competes
// on a CDL team, for instance. This used to be hardcoded per page, which meant
// the admin panel showed one thing and the site showed another, and nobody could
// change it without a code edit. It's now a field on the player.

// Fallback for players imported before the field existed, so their existing
// listings survive without a data migration. Once a player is saved from the
// admin panel their stored value takes over.
const LEGACY_CROSS_LISTINGS = {
  Jrwyd: ["CDL Team A", "Content Creators"],
  Rvssian: ["Content Creators"],
  Skrmxx: ["Content Creators"],
  Peterson: ["Content Creators"],
};

/**
 * Every division a player should appear under, beyond their own.
 * Falls back to the pre-existing listings only while the player has no stored
 * value — an explicit empty array means "nowhere else" and is respected.
 */
export function effectiveAlsoAppearsIn(player) {
  if (!player) return [];
  if (Array.isArray(player.alsoAppearsIn)) return player.alsoAppearsIn;
  return LEGACY_CROSS_LISTINGS[player.slug || player.id] || [];
}

/** Does this player belong on the page for `group`, by division or cross-listing? */
export function isInGroup(player, group) {
  const divisions = DIVISION_GROUPS[group] || [];
  if (divisions.includes(player.division)) return true;
  return (player.alsoAppearsIn || []).some((d) => divisions.includes(d));
}

// ─── Publish state ───────────────────────────────────────────────────────────

/**
 * Is this player visible on the public site right now?
 * Drafts never show. Scheduled players show once their go-live time passes.
 */
export function isLive(player, now = Date.now()) {
  if (!player) return false;
  if (player.status !== "published") return false;
  if (!player.publishAt) return true;
  const at =
    typeof player.publishAt?.toMillis === "function"
      ? player.publishAt.toMillis()
      : player.publishAt;
  return at <= now;
}

/** Label describing a player's publish state, for the admin list. */
export function publishLabel(player) {
  if (player.status !== "published") {
    // Someone who has been live before and is now unpublished has been hidden,
    // not drafted — worth distinguishing so the list reads honestly.
    return player.wasPublished ? "Hidden" : "Draft";
  }
  if (!player.publishAt) return "Live";
  const at =
    typeof player.publishAt?.toMillis === "function"
      ? player.publishAt.toMillis()
      : player.publishAt;
  if (at <= Date.now()) return "Live";
  return `Scheduled ${new Date(at).toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
  })}`;
}

// ─── Reads ───────────────────────────────────────────────────────────────────

/** Live subscription to every player, including drafts. For the admin panel. */
export function subscribeToAllPlayers(callback, onError) {
  return onSnapshot(
    PLAYERS_COL,
    (snap) => {
      const list = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
      list.sort(
        (a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0) || (a.name || "").localeCompare(b.name || "")
      );
      callback(list);
    },
    onError
  );
}

// ─── Writes ──────────────────────────────────────────────────────────────────

/** Shape a form object into a Firestore document. */
function toDoc(form) {
  return {
    slug: (form.slug || form.name || "").trim(),
    name: (form.name || "").trim(),
    division: form.division || "CDL",
    age: form.age === "" || form.age == null ? "" : form.age,
    country: (form.country || "").trim(),
    joined: (form.joined || "").trim(),
    specialization: (form.specialization || "").trim(),
    achievements: (form.achievements || []).map((a) => a.trim()).filter(Boolean),
    bio: (form.bio || "").trim(),
    socials: Object.fromEntries(
      Object.entries(form.socials || {})
        .map(([k, v]) => [k, (v || "").trim()])
        .filter(([, v]) => v)
    ),
    youtubeUrl: (form.youtubeUrl || "").trim(),
    imageUrl: form.imageUrl || "",
    imagePath: form.imagePath || "",
    imagePosition: form.imagePosition || "",
    // Which bundled photo this player uses, for those imported from the old
    // static roster. Stored once at import and carried through every edit, so
    // renaming a player doesn't lose their picture.
    legacyImageKey: form.legacyImageKey || "",
    status: form.status === "published" ? "published" : "draft",
    publishAt: form.publishAt ? Timestamp.fromMillis(form.publishAt) : null,
    sortOrder: Number(form.sortOrder) || 0,
    // Sticky once true, so we can tell "never published" from "taken down".
    wasPublished: Boolean(form.wasPublished) || form.status === "published",
    // Extra pages this player shows on, beyond their own division.
    alsoAppearsIn: Array.isArray(form.alsoAppearsIn) ? form.alsoAppearsIn : [],
    // Carried through edits; stamped on first publish (see createPlayer/updatePlayer).
    firstPublishedAt: form.firstPublishedAt ?? null,
    // Manual override for someone who isn't really a new arrival — an existing
    // player re-added under a different division, for instance.
    excludeFromNewMembers: Boolean(form.excludeFromNewMembers),
    // Graduates only: the pro team they moved on to, shown as "Now with …".
    currentTeam: (form.currentTeam || "").trim(),
  };
}

/**
 * When a player actually became visible on the site.
 *
 * Deliberately not createdAt: someone drafted on Monday and published on Friday
 * became a "new member" on Friday, and counting from when the record was typed
 * would eat most of their time in the new-members announcement.
 *
 * A scheduled player's go-live moment is their publishAt; otherwise it's when
 * they were first published. Falls back to createdAt for older records.
 */
export function liveSince(player) {
  const ms = (v) =>
    typeof v?.toMillis === "function" ? v.toMillis() : typeof v === "number" ? v : null;
  return ms(player?.publishAt) ?? ms(player?.firstPublishedAt) ?? ms(player?.createdAt);
}

/**
 * Persist a new running order for one division.
 * Positions are rewritten as 0..n for that division only — division sections
 * have their own fixed order (DIVISION_ORDER), so renumbering one group can't
 * disturb the others.
 */
export async function reorderDivision(orderedPlayers) {
  const batch = writeBatch(db);
  orderedPlayers.forEach((player, index) => {
    batch.update(doc(PLAYERS_COL, player.id), {
      sortOrder: index,
      updatedAt: serverTimestamp(),
    });
  });
  await batch.commit();
}

export async function createPlayer(form) {
  const doc = toDoc(form);
  // Imported players occupy 0..n, so give new arrivals a trailing sort value.
  // Otherwise they'd sort to position 0 and yank their whole division group to
  // the top of the admin list, which reads as the page rearranging itself.
  if (!doc.sortOrder) doc.sortOrder = Date.now();
  // Published straight away, so this is the moment they went live.
  if (doc.status === "published" && !doc.publishAt && !doc.firstPublishedAt) {
    doc.firstPublishedAt = serverTimestamp();
  }
  const ref = await addDoc(PLAYERS_COL, {
    ...doc,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });
  return ref.id;
}

export async function updatePlayer(id, form) {
  const data = toDoc(form);
  // Stamp the go-live moment only on a genuine first publish. `form.wasPublished`
  // is the state before this edit, so an existing player being edited — moved to
  // another division, say — is not treated as newly arrived and won't reappear in
  // the new-members announcement.
  const publishedBefore = Boolean(form.wasPublished);
  if (
    data.status === "published" &&
    !data.publishAt &&
    !data.firstPublishedAt &&
    !publishedBefore
  ) {
    data.firstPublishedAt = serverTimestamp();
  }
  await updateDoc(doc(PLAYERS_COL, id), {
    ...data,
    updatedAt: serverTimestamp(),
  });
}

/** Delete a player and their uploaded photo. */
export async function removePlayer(player) {
  if (player.imagePath) await deletePlayerImage(player.imagePath);
  await deleteDoc(doc(PLAYERS_COL, player.id));
}

// ─── One-time migration ──────────────────────────────────────────────────────

/**
 * Copy the original playersData.js roster into Firestore.
 *
 * Photos are NOT re-uploaded — those players keep using the images bundled with
 * the app, matched by name (see usePlayers). Only players added from the admin
 * panel from here on get Storage-hosted photos.
 *
 * Refuses to run if the collection already has documents, so it can't be
 * fired twice and create duplicates.
 */
export async function seedFromStaticData() {
  const existing = await getDocs(PLAYERS_COL);
  if (!existing.empty) {
    throw new Error(
      `Roster already imported (${existing.size} players). Delete them first if you need to re-import.`
    );
  }

  // Imported players are backdated so they don't all flood the "new members"
  // announcement, which derives itself from createdAt.
  const backdated = Timestamp.fromMillis(Date.now() - 365 * 24 * 60 * 60 * 1000);

  const batch = writeBatch(db);
  staticPlayers.forEach((p, index) => {
    batch.set(doc(PLAYERS_COL), {
      ...toDoc({
        slug: p.id,
        name: p.name,
        division: p.division,
        age: p.age ?? "",
        country: p.country ?? "",
        joined: p.joined ?? "",
        specialization: p.specialization ?? "",
        achievements: p.achievements ?? [],
        bio: p.bio ?? "",
        socials: p.socials ?? {},
        // Pin the bundled photo by the name it was filed under at import time.
        legacyImageKey: p.name,
        status: "published",
        sortOrder: index,
      }),
      title: p.title ?? "",
      createdAt: backdated,
      updatedAt: serverTimestamp(),
    });
  });
  await batch.commit();
  return staticPlayers.length;
}
